import { action, computed, makeObservable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { PresetViewSupportedView } from '@bringg/types';

import { CONFIG_TABLE_IDS } from 'bringg-web/consts';
import { TableConfigObject } from './table-config-object';
import { AdditionalFilters } from './table-config-utils';
import RootStore from '../root-store';
import { TASK_ALERTS_EMPTY_FILTERS } from './alerts-view-store';
import { ExceptionFilters, TranslatableOption } from 'bringg-web/types/common.consts';
import { hasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { PRESETS_VIEWS_FEATURE_FLAG, getActivePresetViewForView } from 'bringg-web/features/preset-views/utils';
import { ALWAYS_ACTIVE_FIRST_COLUMNS } from 'bringg-web/features/exceptions-management/columns/column-utils';
import type { AlertsPresetView, PresetViewAlertTableConfig } from 'bringg-web/features/preset-views/stores/stores';

// This adapter will be used for controlling Alert tables config along with Preset View
// while this adapter empty and serve as Proxy to FilterStore
// depending on FF this service will decide either to use AlertsViewStore or PresetViewStore as source of table config
export class TableConfigObjectAdapter extends TableConfigObject {
	presetsViewType: PresetViewSupportedView;
	presetViewsEnabled = false;

	constructor(
		tableId: CONFIG_TABLE_IDS,
		defaultFilters: ExceptionFilters,
		defaultOrderedColumns: TranslatableOption[],
		presetsViewType: PresetViewSupportedView
	) {
		super(tableId, defaultFilters, defaultOrderedColumns);
		const {
			usersStore: { currentUser }
		} = getRoot<RootStore>().data;
		this.presetViewsEnabled = hasFeatureFlag(currentUser, PRESETS_VIEWS_FEATURE_FLAG);

		makeObservable(this, {
			setTableFilters: action,
			setNewTableFilters: action,
			resetTableFilters: action,
			setTableGroupBy: action,
			setTableOrderedColumns: action,
			setTableSortBy: action,
			setTableColumnWidths: action,
			toggleHideTableColumn: action,
			setAdditionalTableFiltersNames: action,
			activePreset: computed
		});

		this.presetsViewType = presetsViewType;
	}

	get tableFilters(): typeof this.filters {
		return this.filters;
	}

	setTableFilters: typeof this.setFilters = filters => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateFilters(filters);
		}
		this.setFilters(filters);
	};

	setNewTableFilters: typeof this.setNewFilters = partialFilters => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateFilters(Object.assign({}, this.defaultFilters, partialFilters));
		}

		this.setNewFilters(partialFilters);
	};

	resetTableFilters: typeof this.resetFilters = (fieldName?) => {
		if (this.presetViewsEnabled) {
			if (fieldName) {
				this.activePreset.updateFilters({
					...this.activePreset.filters,
					[fieldName]: this.defaultFilters[fieldName]
				});
			} else {
				this.activePreset.updateFilters(this.defaultFilters);
			}
		}

		this.resetFilters(fieldName);
	};

	get tableNormalizedFilters(): typeof this.normalizedFilters {
		return this.normalizedFilters;
	}

	get tableGroupBy(): typeof this.groupBy {
		return this.groupBy;
	}

	setTableGroupBy: typeof this.setGroupBy = ids => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateGroupBy(ids);
		}
		this.setGroupBy(ids);
	};

	get tableColumnOrder(): typeof this.columnOrder {
		return this.columnOrder;
	}

	get tableOrderedColumns(): typeof this.orderedColumns {
		if (this.presetViewsEnabled) {
			return this.activePreset.orderedColumns;
		}
		return this.orderedColumns;
	}

	setTableOrderedColumns: typeof this.setOrderedColumns = columns => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateOrderedColumns(columns);
		}
		this.setOrderedColumns(columns);
	};

	get defaultTableOrderedColumns(): typeof this.defaultOrderedColumns {
		return this.defaultOrderedColumns;
	}

	get tableSortBy(): typeof this.sortBy {
		return this.sortBy;
	}

	setTableSortBy = sortBy => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateSortBy(sortBy);
		}

		this.setSortBy(sortBy);
	};

	get tableColumnWidths() {
		if (this.presetViewsEnabled) {
			return this.activePreset?.columnWidths || {};
		}
		return this.columnWidths;
	}

	setTableColumnWidths = (columnWidths: Record<string, number>) => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateColumnsWidth(columnWidths);
		} else {
			this.setColumnWidths(columnWidths);
		}
	};

	resetTableColumns: typeof this.resetColumns = () => {
		if (this.presetViewsEnabled) {
			this.activePreset.resetColumns(this.defaultOrderedColumns);
		}

		this.resetColumns();
	};

	get hiddenTableColumnsSet(): typeof this.hiddenColumnsSet {
		return this.hiddenColumnsSet;
	}

	get hiddenTableColumns(): typeof this.hiddenColumns {
		return this.hiddenColumns;
	}

	toggleHideTableColumn: typeof this.toggleHideColumn = id => {
		if (this.presetViewsEnabled) {
			this.activePreset.toggleColumn(id, this.hiddenColumnsSet.has(id));
		}

		this.toggleHideColumn(id);
	};

	setAdditionalTableFiltersNames = (filtersKey: AdditionalFilters) => {
		if (this.presetViewsEnabled) {
			this.activePreset.updateFilters({
				...this.activePreset.filters,
				additionalFiltersNames: filtersKey
			});
		}

		this.setAdditionalFiltersNames(filtersKey);
	};

	getPresetViewTableConfig = (): PresetViewAlertTableConfig => {
		// to filter out columns that are deleted in App but was saved in preset
		const presetColumns = this.defaultOrderedColumns.map(defaultCol => {
			// columns in default PresetView have `translate_key: undefined`
			const presetCol = this.activePreset.columnsMap.get(defaultCol.id);
			return {
				active: presetCol?.active || Boolean(ALWAYS_ACTIVE_FIRST_COLUMNS.includes(defaultCol.id)),
				id: defaultCol.id,
				position: presetCol?.position || undefined,
				width: presetCol?.width || undefined,
				translateKey: defaultCol.translateKey,
				translate_key: defaultCol.translateKey
			};
		});

		return {
			groupBy: this.activePreset.groupBy,
			filters: { ...TASK_ALERTS_EMPTY_FILTERS, ...this.activePreset.filters },
			sortBy: this.activePreset.sortBy,
			additionalFiltersNames: this.activePreset.filters?.additionalFiltersNames ?? [],
			fields: presetColumns
		};
	};

	get activePreset(): AlertsPresetView {
		return getActivePresetViewForView(this.presetsViewType) as AlertsPresetView;
	}
}
